import { defineStore } from 'pinia';
import { adminApi } from '@/api/admin';
import type { SecretSummaryDto, SecretWithValueDto } from '@/api/types';
import { useEditorContextStore } from '@/stores/editorContext';
import { readSession, writeSession } from '@/lib/sessionPersist';

// Identity for a per-slot secret entry: (slot, scope:name). Used as cache key for revealed
// values and for editor lookups. The encoding mirrors how the API path is constructed.
const cacheKey = (slot: string, scopeColonName: string): string =>
  `${slot}|${scopeColonName}`;

const FILTER_SESSION_KEY = 'secrets-view.filter-text';

interface SecretsState {
  list: SecretSummaryDto[];
  loading: boolean;
  error: string | null;
  // Operator's current scope:name filter text. Survives in-tab navigation and reloads via
  // sessionStorage; discarded when the tab closes.
  filterText: string;
  revealed: Record<string, SecretWithValueDto | undefined>;
}

export const useSecretsStore = defineStore('secrets', {
  state: (): SecretsState => ({
    list: [],
    loading: false,
    error: null,
    filterText: readSession<string>(FILTER_SESSION_KEY, ''),
    revealed: {},
  }),
  actions: {
    setFilterText(value: string) {
      this.filterText = value;
      writeSession(FILTER_SESSION_KEY, value);
    },
    async load() {
      this.loading = true;
      this.error = null;
      try {
        this.list = await adminApi.listSecrets();
      } catch (e) {
        this.error = (e as Error).message;
      } finally {
        this.loading = false;
      }
    },
    async reveal(slot: string, scopeColonName: string) {
      const value = await adminApi.getSecret(slot, scopeColonName);
      this.revealed = { ...this.revealed, [cacheKey(slot, scopeColonName)]: value };
      return value;
    },
    hide(slot: string, scopeColonName: string) {
      const next = { ...this.revealed };
      delete next[cacheKey(slot, scopeColonName)];
      this.revealed = next;
    },
    async save(slot: string, scopeColonName: string, value: string) {
      await adminApi.setSecret(slot, scopeColonName, value);
      this.hide(slot, scopeColonName);
      // Refresh (not just invalidate) so the local Monaco completion / marker cache picks
      // up the new entry without needing a keystroke to trigger a re-warm.
      void useEditorContextStore().refreshSlot(slot);
      await this.load();
    },
    async remove(slot: string, scopeColonName: string) {
      await adminApi.deleteSecret(slot, scopeColonName);
      this.hide(slot, scopeColonName);
      void useEditorContextStore().refreshSlot(slot);
      await this.load();
    },
  },
});
